import React, { useEffect, useState, useCallback } from 'react';
import dateFormat from 'dateformat';
import Radio from '@mui/material/Radio';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import DialogActions from '@mui/material/DialogActions';
import { downloadFile } from '#action/download';
import {
  StyledDialog,
  StyledDialogTitle,
  CloseIconButton,
  StyledDialogContent,
  ReportRadioGroup
} from './styles';
import buildReportPath, { outputFormatOptions } from './build-report-path';

const defaultContentOptions = [
  {
    name: 'locations',
    label: 'Map / Locations',
    checked: false
  },
  {
    name: 'temperatureChart',
    label: 'Temperature chart',
    checked: true,
    disabled: true
  },
  {
    name: 'humidityChart',
    label: 'Humidity chart',
    checked: false
  },
  {
    name: 'statisticalSummary',
    label: 'Statistical summary',
    checked: true
  },
  {
    name: 'alertsTable',
    label: 'Alerts table',
    checked: true,
    disabled: true
  },
  {
    name: 'measurementsTable',
    label: 'Measurements table',
    checked: false
  }

];

const ReportDialog = props => {
  const {
    showDialog,
    setReportDialog,
    appliance: { id: applianceId, label },
    dispatch,
    account,
    dateRange: { start, end }
  } = props;
  const [measurementSystemValue, setMeasurementSystem] = useState(
    account?.measurementSystem
  );
  const [outputFormat, setOutputFormat] = useState(outputFormatOptions.PDF);
  const [contentOptions, setContentOptions] = useState(defaultContentOptions);

  const startDate = new Date(start).toISOString();
  const endDate = new Date(end).toISOString();

  useEffect(() => setMeasurementSystem(account?.measurementSystem), [account]);

  const closeDialog = () => setReportDialog(false);
  const handleContentOptionsChange = useCallback(e => {
    const updatedContentOptions = contentOptions.map(option =>
      (option.name === e.target.name ? { ...option, checked: e.target.checked } : option));
    setContentOptions(updatedContentOptions);
  }, [contentOptions]);

  const handleExportClick = async () => {
    closeDialog();
    const path = buildReportPath({
      outputFormat,
      measurementSystem: measurementSystemValue,
      applianceId,
      startDate,
      endDate,
      contentOptions
    });

    const fileName = `${label}_${dateFormat(start, 'dd-mm-yyyy')}-${dateFormat(end, 'dd-mm-yyyy')}${
      outputFormat === outputFormatOptions.CSV ? '.csv' : '.pdf'
    }`;

    await dispatch(downloadFile(path, fileName));
  };

  return (
    <StyledDialog
      open={showDialog}
      onClose={closeDialog}
      aria-labelledby="report-dialog"
      aria-describedby="report-dialog"
      onClick={e => e.stopPropagation()}
    >
      <StyledDialogTitle>
        Report options
        <CloseIconButton
          aria-label="close"
          onClick={closeDialog}
        >
          <CloseIcon />
        </CloseIconButton>
      </StyledDialogTitle>
      <StyledDialogContent>
        <Typography variant="h6">Units</Typography>
        <ReportRadioGroup
          row
          aria-label="Measurement system"
          name="measurementSystem"
          value={measurementSystemValue}
          onChange={e => setMeasurementSystem(e.target.value)}
        >
          <FormControlLabel value="metric" control={<Radio />} label="Metric (km, °C)" />
          <FormControlLabel value="us" control={<Radio />} label="Imperial (mi, °F)" />
        </ReportRadioGroup>
        <Typography variant="h6">Output format</Typography>
        <ReportRadioGroup
          row
          aria-label="outputFormat"
          name="outputFormat"
          value={outputFormat}
          onChange={e => setOutputFormat(e.target.value)}
        >
          {Object.entries(outputFormatOptions).map(([key, value]) =>
            <FormControlLabel
              key={key}
              value={value}
              control={<Radio />}
              label={value.toUpperCase()}
            />)}
        </ReportRadioGroup>
        {outputFormat === outputFormatOptions.PDF &&
          <>
            <Typography variant="h6">Content</Typography>
            <FormGroup>
              {contentOptions
                .map(({ name, label: formControlLabel, checked, disabled }) => (
                  <FormControlLabel
                    key={name}
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={handleContentOptionsChange}
                        name={name}
                        disabled={disabled}
                      />
                    }
                    label={formControlLabel}
                  />
                ))}
            </FormGroup>
          </>}
      </StyledDialogContent>
      <DialogActions>
        <Button
          onClick={closeDialog}
          variant="outlined"
          color="primary"
        >
          Cancel
        </Button>
        <Button
          onClick={handleExportClick}
          variant="contained"
          color="secondary"
          autoFocus
        >
          Download
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default ReportDialog;
